/**
 * The gun: a rotary cannon, its rounds, and where they go.
 *
 * ## The rate problem, which is the only structurally interesting part
 *
 * Six thousand rounds a minute is a hundred rounds a second, and the physics tick is 120 Hz. So a
 * tick is 0.83 of a round — the gun fires *less than once per tick*, and any implementation that
 * fires "a round per tick while the trigger is down" produces 120 rounds a second on this build
 * and a different number on any other. Worse, an implementation that fires `floor(rate·dt)` rounds
 * fires zero every tick, forever, because 0.83 floors to 0.
 *
 * So the gun carries a fractional accumulator: time in, rounds out, remainder kept. It is the same
 * mechanism the fixed-step scheduler uses on the frame clock and it is correct for the same
 * reason — the cyclic rate is a property of the gun, and the tick rate must not be able to change
 * it. A future 20 Hz network tick would fire the same hundred rounds a second, in bigger clumps.
 *
 * Sub-tick timing is then reconstructed rather than thrown away: within a tick the rounds are
 * spread along the aircraft's own travel, so a burst leaves a smooth line of tracer instead of
 * eight rounds stacked at one point. That costs one lerp per round and is the difference between
 * a tracer stream and a dotted line.
 *
 * ## Rounds inherit the aeroplane's velocity, and this matters
 *
 * A round leaves the barrel at 1036 m/s *relative to the gun*, and the gun is doing 250 m/s. In
 * a turn the aircraft's velocity is not along its nose, so the rounds do not fly where the nose
 * points — they fly where the nose points plus wherever the aeroplane was already going. That
 * offset is exactly what the lead-computing sight in `lcos.ts` exists to correct for, and both
 * halves have to be modelled or the sight is correcting for nothing.
 *
 * ## Flat ground
 *
 * A round is culled when it falls below the ground elevation the caller supplies, which is the
 * elevation under the *aircraft*. Over the 3 km and 4 s a round lives, that is a flat-earth
 * approximation of the terrain, and it is a deliberate one: the honest alternative is a geodetic
 * conversion plus a quadtree lookup per round per tick, which at 400 live rounds is a hundred
 * thousand tile probes a second to decide when to stop drawing something. Rounds that fly into a
 * hillside above the aircraft's own ground level therefore pass through it. Nothing is scored on
 * that, and the case a player sees — firing at a target and watching the misses kick up short —
 * is the case this gets right.
 *
 * `updateFixed(dt)`: yes — {@link Cannon.updateFixed}. Allocation-free after construction; every
 * round comes from a pool filled at load.
 */

import { Vector3 } from 'three';
import type { Rng } from '../../core/rng.js';
import { integrateBallistic, inverseBallisticCoefficient, sampleCone, segmentSphereHit } from './ballistics.js';
import type { CannonConfig } from './config.js';
import type { HitQueue, TargetList } from './engagement.js';

/**
 * One round in flight.
 *
 * A plain struct, pooled by the `active` flag rather than by a free list: the renderer walks the
 * whole array every frame anyway to build the tracer geometry, so a compact array with a liveness
 * flag beats a free list that would scatter the live ones through it.
 */
export interface CannonRound {
  active: boolean;
  /** Every Nth round, per `tracerEvery`. Only tracers are drawn. */
  tracer: boolean;
  /** Sim-frame NED [m]. */
  readonly positionNed: Vector3;
  /** Where it was at the start of the step — the swept hit test and the tracer streak both need it. */
  readonly previousNed: Vector3;
  readonly velocityNed: Vector3;
  ageS: number;
}

function makeRound(): CannonRound {
  return {
    active: false,
    tracer: false,
    positionNed: new Vector3(),
    previousNed: new Vector3(),
    velocityNed: new Vector3(),
    ageS: 0,
  };
}

/** What the gun needs from the aircraft each tick. Owned by the caller and reused. */
export interface GunInput {
  /** Is the trigger down? */
  firing: boolean;
  /** Muzzle position, sim-frame NED [m]. */
  readonly muzzleNed: Vector3;
  /** Unit vector down the bore, sim-frame NED. */
  readonly boreNed: Vector3;
  /** Unit vector to the gun's right, perpendicular to the bore — the dispersion basis. */
  readonly boreRightNed: Vector3;
  /** Unit vector completing the basis. */
  readonly boreUpNed: Vector3;
  /** The launch aircraft's velocity, which the rounds inherit [m/s]. */
  readonly carrierVelocityNed: Vector3;
  /** Air density where the gun is [kg/m³]. */
  densityKgPerM3: number;
  /** Ground elevation below the aircraft [m above the NED datum]. See the module note. */
  groundElevationM: number;
}

export function makeGunInput(): GunInput {
  return {
    firing: false,
    muzzleNed: new Vector3(),
    boreNed: new Vector3(1, 0, 0),
    boreRightNed: new Vector3(0, 1, 0),
    boreUpNed: new Vector3(0, 0, -1),
    carrierVelocityNed: new Vector3(),
    densityKgPerM3: 1.225,
    groundElevationM: 0,
  };
}

const scratchDirection = /* #__PURE__ */ new Vector3();

export class Cannon {
  readonly config: CannonConfig;
  /** The whole pool. Walk it and skip `!active`; the renderer does exactly that. */
  readonly rounds: CannonRound[] = [];

  private readonly rng: Rng;
  private readonly inverseBc: number;
  private readonly roundsPerSecond: number;

  private ammunitionValue: number;
  private fireAccumulator = 0;
  private roundCounter = 0;
  private activeCountValue = 0;
  /** Rolling search cursor for the round pool — see {@link take}. */
  private cursor = 0;
  /** Rounds fired this session, for the score screen and the hit-rate readout. */
  private firedValue = 0;

  /**
   * @param poolSize Live-round ceiling. Rate × lifetime is the steady-state population — 100/s for
   *   4 s is 400 — and the pool is that plus headroom, so a held trigger never allocates.
   */
  constructor(config: CannonConfig, rng: Rng, poolSize?: number) {
    this.config = config;
    this.rng = rng;
    this.inverseBc = inverseBallisticCoefficient(
      config.roundDiameterM,
      config.roundMassKg,
      config.dragCoefficient,
    );
    this.roundsPerSecond = config.roundsPerMinute / 60;
    this.ammunitionValue = config.capacity;

    const size = poolSize ?? Math.ceil(this.roundsPerSecond * config.lifetimeS * 1.15);
    for (let i = 0; i < size; i++) this.rounds.push(makeRound());
  }

  get ammunition(): number {
    return this.ammunitionValue;
  }

  get roundsFired(): number {
    return this.firedValue;
  }

  get activeRounds(): number {
    return this.activeCountValue;
  }

  /** Inverse ballistic coefficient of this gun's round — the sight needs it. */
  get roundInverseBc(): number {
    return this.inverseBc;
  }

  /** Is the gun dry? */
  get empty(): boolean {
    return this.ammunitionValue <= 0;
  }

  /** Refill the magazine. The pause menu's REARM. Rounds already in the air are left alone. */
  rearm(): void {
    this.ammunitionValue = this.config.capacity;
  }

  /**
   * Carry every round in flight through a floating-origin rebase.
   *
   * A round has no latitude and longitude — it is a position and a velocity in sim-frame NED,
   * integrated in place — so when the origin moves it is left behind in the old frame. Everything
   * it is tested against does *not* get left behind: a gunnery target holds a geodetic anchor and
   * re-derives its NED every tick (`targets.ts`), and the ground height comes from the terrain,
   * which re-derives too. So without this the two halves of the gunnery system end up displaced
   * from each other by the whole shift, and rounds miss by however far the origin just moved.
   *
   * **`previousNed` must move with `positionNed`, and that is the part worth being careful about.**
   * The hit test is swept — it tests the segment from where the round was to where it is — so
   * shifting only the head would stretch that segment across the entire rebase distance for one
   * tick, and a round would register hits on everything along a 500 m ray it never flew down.
   * Velocity is a rate and does not move at all.
   *
   * This was survivable when the threshold was 10 km: at 500 kt a rebase came every 39 s against a
   * round lifetime of about four seconds, so roughly a tenth of bursts were affected and the rest
   * of the time it was invisible. At 500 m the period is 1.9 s, which is shorter than the rounds
   * live — so practically every burst now crosses one. The bug did not change; the exposure did.
   *
   * A bare translation rather than the exact rigid carry (`FloatingOrigin.applyToLocal`), for the
   * same reason the particle systems use one: it is allocation-free and three adds per round
   * against 400 live rounds. The error it accepts is the frame's rotation, `d_out · shift / R` —
   * about 0.3 m at the 4 km a round can reach, and twentyfold smaller than it was before the
   * threshold came down, because it scales with the shift.
   */
  applyOriginShift(deltaNorthM: number, deltaEastM: number, deltaDownM: number): void {
    for (let i = 0; i < this.rounds.length; i++) {
      const round = this.rounds[i];
      if (!round.active) continue;
      round.positionNed.x += deltaNorthM;
      round.positionNed.y += deltaEastM;
      round.positionNed.z += deltaDownM;
      round.previousNed.x += deltaNorthM;
      round.previousNed.y += deltaEastM;
      round.previousNed.z += deltaDownM;
    }
  }

  /** Clear every round in flight — a respawn, or leaving the mode. */
  clear(): void {
    for (let i = 0; i < this.rounds.length; i++) this.rounds[i].active = false;
    this.activeCountValue = 0;
    this.fireAccumulator = 0;
  }

  /** Full reset: ammunition, rounds in the air, and the shot counters. */
  reset(): void {
    this.clear();
    this.ammunitionValue = this.config.capacity;
    this.firedValue = 0;
    this.roundCounter = 0;
  }

  /**
   * Fire, fly and resolve one physics step.
   *
   * @param dt      Step [s].
   * @param input   The aircraft's gun state this tick.
   * @param targets What may be hit. Pass `NO_TARGETS` in free flight.
   * @param hits    Where impacts are reported.
   */
  updateFixed(dt: number, input: GunInput, targets: TargetList, hits: HitQueue): void {
    this.fire(dt, input);
    this.advance(dt, input, targets, hits);
  }

  /** The fractional-rate accumulator. See the module header for why it is not a per-tick spawn. */
  private fire(dt: number, input: GunInput): void {
    if (!input.firing || this.ammunitionValue <= 0) {
      /*
       * Draining the accumulator on release rather than keeping it is deliberate. Kept, it would
       * bank up to one round of credit during a pause and fire it instantly on the next press,
       * so tapping the trigger quickly would produce a *higher* rate than holding it — the
       * classic accumulator bug, and an exploit in a gunnery score.
       */
      this.fireAccumulator = 0;
      return;
    }

    this.fireAccumulator += this.roundsPerSecond * dt;
    let toFire = Math.floor(this.fireAccumulator);
    if (toFire <= 0) return;
    this.fireAccumulator -= toFire;
    if (toFire > this.ammunitionValue) toFire = this.ammunitionValue;

    const coneRad = this.config.dispersionMilliradians * 1e-3;
    for (let i = 0; i < toFire; i++) {
      const round = this.take();
      if (round === null) break;

      this.ammunitionValue--;
      this.firedValue++;
      this.roundCounter++;
      round.tracer = this.roundCounter % this.config.tracerEvery === 0;
      round.ageS = 0;

      sampleCone(
        input.boreNed,
        input.boreRightNed,
        input.boreUpNed,
        coneRad,
        this.rng.nextFloat(),
        this.rng.nextFloat(),
        scratchDirection,
      );

      /*
       * Spread the tick's rounds along the aircraft's travel.
       *
       * `fraction` is how far back in the tick this round left the barrel: the first of the batch
       * is oldest. Rewinding the muzzle by that much of the carrier's velocity puts the rounds
       * where the gun *was* when each fired, which is what makes a burst a continuous stream
       * rather than clumps at 120 Hz.
       */
      const fraction = (toFire - i) / toFire;
      const rewind = fraction * dt;
      round.positionNed.set(
        input.muzzleNed.x - input.carrierVelocityNed.x * rewind,
        input.muzzleNed.y - input.carrierVelocityNed.y * rewind,
        input.muzzleNed.z - input.carrierVelocityNed.z * rewind,
      );
      round.previousNed.copy(round.positionNed);

      const muzzle = this.config.muzzleVelocityMps;
      round.velocityNed.set(
        input.carrierVelocityNed.x + scratchDirection.x * muzzle,
        input.carrierVelocityNed.y + scratchDirection.y * muzzle,
        input.carrierVelocityNed.z + scratchDirection.z * muzzle,
      );
    }
  }

  /** Integrate every live round, then test what it swept through. */
  private advance(dt: number, input: GunInput, targets: TargetList, hits: HitQueue): void {
    const lifetime = this.config.lifetimeS;
    const groundZ = -input.groundElevationM;
    const damage = this.config.damagePerRound;

    for (let i = 0; i < this.rounds.length; i++) {
      const round = this.rounds[i];
      if (!round.active) continue;

      round.previousNed.copy(round.positionNed);
      integrateBallistic(
        round.positionNed,
        round.velocityNed,
        this.inverseBc,
        input.densityKgPerM3,
        dt,
      );
      round.ageS += dt;

      // --- what did it pass through? ------------------------------------------------------
      let consumed = false;
      for (let t = 0; t < targets.length; t++) {
        const target = targets.at(t);
        if (!target.alive) continue;
        const fraction = segmentSphereHit(
          round.previousNed,
          round.positionNed,
          target.positionNed,
          target.radiusM,
        );
        if (fraction < 0) continue;

        const report = hits.push();
        if (report !== null) {
          report.targetId = target.id;
          report.kind = 'cannon';
          report.damage = damage;
          report.positionNed.lerpVectors(round.previousNed, round.positionNed, fraction);
          report.speedMps = round.velocityNed.length();
          report.directionNed
            .copy(round.velocityNed)
            .multiplyScalar(report.speedMps > 1e-6 ? 1 / report.speedMps : 0);
        }
        consumed = true;
        break;
      }

      if (consumed || round.ageS >= lifetime || round.positionNed.z >= groundZ) {
        this.release(round);
      }
    }
  }

  /**
   * The next free slot, found from a rolling cursor rather than from index 0.
   *
   * Rounds are created and retired in very nearly the same order — they all live exactly as long
   * as each other unless they hit something — so the slot after the last one taken is almost
   * always free. Scanning from 0 instead would walk the live population every time, which at a
   * held trigger is 400 slots × 100 rounds a second of pure searching.
   */
  private take(): CannonRound | null {
    const size = this.rounds.length;
    for (let scanned = 0; scanned < size; scanned++) {
      const index = this.cursor;
      this.cursor = index + 1 >= size ? 0 : index + 1;
      const round = this.rounds[index];
      if (round.active) continue;
      round.active = true;
      this.activeCountValue++;
      return round;
    }
    return null;
  }

  private release(round: CannonRound): void {
    round.active = false;
    this.activeCountValue--;
  }
}
